import { Bar, BarConfig } from '@ant-design/plots';
import { useQuery } from '@apollo/client';
import { GET_INSTITUTIONS_BY_REGION } from '@frontend/data-access';
import { Card } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';

function PopularRegionStats({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  const { data, loading } = useQuery<{
    companyCountByRegion: { region: string; count: number }[];
  }>(GET_INSTITUTIONS_BY_REGION);

  const highestRegion = useMemo(() => {
    if (!data?.companyCountByRegion || data.companyCountByRegion.length === 0)
      return '';
    return data.companyCountByRegion.reduce((max, item) =>
      item.count > max.count ? item : max
    ).region;
  }, [data]);

  const formattedData = useMemo(() => {
    return data?.companyCountByRegion.map((item) => ({
      ...item,
      color: item.region === highestRegion ? '#63E063' : '#58AFFF',
    }));
  }, [data, highestRegion]);

  const config: BarConfig = {
    data: formattedData || [],
    loading: loading,
    xField: 'region',
    yField: 'count',
    paddingRight: 20,
    colorField: 'color',
    scale: {
      color: {
        range: ['#63E063', '#58AFFF'],
      },
    },
    interaction: {
      tooltip: {
        name: 'region',
        render: (event, { title, items }) => {
          return (
            <div>
              <div>{title}</div>
              <div>
                {t('count')}: {items[0].value}
              </div>
            </div>
          );
        },
      },
    },
    label: {
      text: 'count',
      position: 'right',
      textAlign: 'start',
      dx: 8,
      style: {
        fill: 'rgba(0, 0, 0, 0.65)',
        fontFamily: 'Lufga',
      },
    },
    xAxis: {
      label: {
        autoHide: true,
        autoRotate: false,
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
        },
      },
    },
    legend: {
      position: 'bottom',
      flipPage: false,
    },
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
      className={className}
    >
      <div className="mb-5">
        <h2>{t('companies_by_region')}</h2>
        <span className="text-gray-700 font-normal">
          {t('user_region_double_counted')}
        </span>
      </div>
      <Bar key="companies-by-region" {...config} />
    </Card>
  );
}

export default PopularRegionStats;
